// TDU stage-map — ETA model (pure, timezone-safe).
//
// Wall-clock is handled as minutes-after-midnight in the race's local tz (ACDT) — never a
// Date/UTC round-trip — so an editor in any browser tz gets the same posted ETA (the same
// class of bug the search date round-trip hit). Simple mode is the default;
// terrain mode is provisional and gated on the §7 back-test.

/** Simple mode (default, §7): elapsed hours = distance / average speed. */
export function elapsedHoursSimple(distanceKm, avgSpeedKmh) {
    if (!avgSpeedKmh || avgSpeedKmh <= 0) return 0;
    return distanceKm / avgSpeedKmh;
}

/**
 * Terrain-aware mode (provisional, §7). segments = [{ distanceKm, gradientPct }].
 * Classifies each segment by gradient vs threshold and applies a per-class speed.
 */
export function elapsedHoursTerrain(segments, { climbKmh, flatKmh, descentKmh, gradientThresholdPct = 3 }) {
    let hours = 0;
    for (const seg of segments) {
        let speed = flatKmh;
        if (seg.gradientPct >= gradientThresholdPct) speed = climbKmh;
        else if (seg.gradientPct <= -gradientThresholdPct) speed = descentKmh;
        if (speed > 0) hours += seg.distanceKm / speed;
    }
    return hours;
}

/**
 * Parse a clock string to minutes-after-midnight. Forgiving of what editors / Umbraco emit:
 * "10:40am", "1:35 PM", "13:35", "10:40:00 AM", or a datetime like "18/11/2025 10:40 AM"
 * (the time is extracted). Returns NaN for empty/garbage or an empty DateTime (year 0001).
 */
export function parseClock(str) {
    if (typeof str === 'number') return str;
    if (str == null) return NaN;
    const s = String(str).trim();
    if (!s || /\b0001\b/.test(s)) return NaN; // empty / DateTime.MinValue
    const m = s.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap]\.?m\.?)?/i);
    if (!m) return NaN;
    let h = Number(m[1]);
    const min = Number(m[2]);
    const ap = m[3] ? m[3].toLowerCase().replace(/\./g, '') : null;
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    if (h > 23 || min > 59) return NaN;
    return h * 60 + min;
}

/** Default neutral roll-out speed (km/h) when a stage doesn't set one — the TDU norm (the CSV's "Neutral 30"). */
export const DEFAULT_NEUTRAL_KMH = 30;

/**
 * The official-start anchor (minutes-after-midnight) every race ETA is measured from — i.e. the END
 * of the race neutral. Priority:
 *   1. a manually entered end-of-neutral time wins (full editor control / back-compat);
 *   2. otherwise derive it from the roll-out (neutral) start + the neutral rollout at the neutral
 *      speed — the organiser's method (e.g. 10:40 + 1.8 km ÷ 30 km/h ≈ 10:43.6);
 *   3. otherwise the roll-out time itself (may be NaN → ETA shows "—").
 * Returned CONTINUOUS (not pre-rounded) so etaClockMinutes floors it consistently with every other
 * passing time, reproducing the published sheet (643.6 → floored 10:43, not rounded 10:44).
 */
export function resolveStartMinutes({ raceStartMinutes, rollOutMinutes, neutralEndKm = 0, neutralSpeedKmh = 0 } = {}) {
    if (Number.isFinite(raceStartMinutes)) return raceStartMinutes;
    if (Number.isFinite(rollOutMinutes) && neutralEndKm > 0) {
        const speed = neutralSpeedKmh > 0 ? neutralSpeedKmh : DEFAULT_NEUTRAL_KMH;
        return rollOutMinutes + (neutralEndKm / speed) * 60;
    }
    return rollOutMinutes;
}

/**
 * ETA as minutes-after-midnight = race start + elapsed, TRUNCATED to the whole minute.
 * Truncation (not rounding) reproduces the organiser's published times exactly — e.g. Men's
 * 128.705/44 = 2 h 55.5 m is published as 2 h 55 m → 1:35 pm (see §7 "circular" note) — and is
 * the standard elapsed-time display convention.
 */
export function etaClockMinutes(startMinutes, elapsedHours) {
    return Math.floor(startMinutes + elapsedHours * 60);
}

/**
 * Format minutes-after-midnight. Default "1:35 PM"; set ampm=false for "13:35".
 * Optional tzLabel appends e.g. " ACDT".
 */
export function formatClock(minutes, { ampm = true, tzLabel = '' } = {}) {
    if (!Number.isFinite(minutes)) return '—'; // start time not set / unparseable
    const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
    let h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    const mm = String(m).padStart(2, '0');
    const suffix = tzLabel ? ` ${tzLabel}` : '';
    if (!ampm) return `${String(h).padStart(2, '0')}:${mm}${suffix}`;
    const ap = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mm} ${ap}${suffix}`;
}

/** Convenience: distance → formatted Simple-mode ETA string. */
export function etaSimpleAt(distanceKm, avgSpeedKmh, startMinutes, fmt) {
    const eta = etaClockMinutes(startMinutes, elapsedHoursSimple(distanceKm, avgSpeedKmh));
    return formatClock(eta, fmt);
}

/**
 * Spectator passing window around a point's lead-rider ETA (all values minutes-after-midnight).
 *  - leadMin    : the front of the race. This is what avgSpeedKmh already models (the eventual
 *                 winner's average pace), so it's the existing single ETA.
 *  - tailMin    : the last riders / grupetto = leadMin + ridersTailMin.
 *  - caravanMin : the publicity caravan, which precedes the race by caravanLeadMin (null if 0).
 *
 * Deliberately invents NO spread: both offsets are editor-supplied. A roadside spectator plans
 * road-crossing and arrival around these times, so a falsely-precise auto-guess would be a safety
 * risk, not a convenience. Defaults (0/0) collapse the window back to the single lead ETA.
 */
export function etaWindow(etaMinutes, { caravanLeadMin = 0, ridersTailMin = 0 } = {}) {
    if (!Number.isFinite(etaMinutes)) {
        return { caravanMin: null, leadMin: NaN, tailMin: NaN, hasWindow: false };
    }
    return {
        caravanMin: caravanLeadMin > 0 ? etaMinutes - caravanLeadMin : null,
        leadMin: etaMinutes,
        tailMin: ridersTailMin > 0 ? etaMinutes + ridersTailMin : etaMinutes,
        hasWindow: ridersTailMin > 0,
    };
}

/**
 * Format a passing window. No tail → a single time ("1:35 PM"). With a tail → a range that shares
 * one am/pm + tz suffix when both ends agree ("1:35–1:47 PM ACDT"), else both are shown in full.
 */
export function formatEtaWindow({ leadMin, tailMin } = {}, fmt = {}) {
    if (!Number.isFinite(leadMin)) return '—';
    if (!Number.isFinite(tailMin) || tailMin <= leadMin) return formatClock(leadMin, fmt);
    const tailFull = formatClock(tailMin, fmt);
    if (fmt.ampm === false) {
        return `${formatClock(leadMin, { ...fmt, tzLabel: '' })}–${tailFull}`;
    }
    const ampmOf = (s) => (/(AM|PM)/i.exec(s) || [''])[0].toUpperCase();
    if (ampmOf(formatClock(leadMin, fmt)) === ampmOf(tailFull)) {
        const leadCompact = formatClock(leadMin, { ...fmt, tzLabel: '' }).replace(/\s*(AM|PM)$/i, '');
        return `${leadCompact}–${tailFull}`;
    }
    return `${formatClock(leadMin, fmt)}–${tailFull}`;
}
